import { ASSISTANT_PAGE_PATH } from '@components/Assistant/assistantBridge'
import {
  AssistantPageContext,
  caseListCommand,
  isRecordId,
  pageContextFromPath,
  pageContextKey,
  pageContextListTool,
  pageContextToQuery,
  pageContextTool,
} from '@components/Assistant/assistantPageContext'
import {
  AVAILABILITY_ERROR_CODES,
  isRefusedBeforeStored,
  PRIVACY_NOTICE_REQUIRED,
  RECONCILED_ERROR_CODES,
  RETRYABLE_ERROR_CODES,
  TURN_ENGINE_ERROR_CODES,
} from '@components/Assistant/errorMessages'
import type { AssistantPlacement } from '@components/Assistant/placements/types'
import { ApiService } from '@services/ApiService'
import type { CreateAssistantConversationBody } from '@services/modules/Assistant'
import {
  AssistantClarificationOption,
  streamAssistantMessage,
} from '@services/modules/AssistantStream'
import dashboardStore from '@stores/dashboard'
import {
  IReactionDisposer,
  makeAutoObservable,
  reaction,
  runInAction,
} from 'mobx'

import {
  isExecuting,
  isOutcomeUnknown,
  toChatMessage,
  withActionStatus,
} from './assistantActions'
import { AssistantPlacementHistory } from './assistantPlacementHistory'
import {
  findStoredTurn,
  placeOfQuestion,
  QuestionPlace,
} from './assistantReconcile'
// Types only: the store creates the sessions, so a value import back would be circular.
import type {
  AssistantChatMessage,
  AssistantDraftMention,
  AssistantFeedbackDetails,
  AssistantFeedbackRating,
  AssistantStore,
  AssistantSuggestedQuery,
} from './assistantStore'
import {
  initialTurn,
  TurnEvent,
  turnReducer,
  turnToMessage,
  TurnState,
} from './assistantTurn'

/** The most records one message may tag; the server refuses a message with more. */
const MAX_MENTIONS_PER_MESSAGE = 10

/**
 * How long after a turn was cut off the server is asked what it kept of it. It needs that long
 * to store the turn's last row, and a read that follows a write too closely may not see it.
 */
export const RECONCILE_DELAY_MS = 6000

/**
 * The waits before the server is asked again when that read itself failed: right after a dropped
 * connection or a restart the server usually cannot be reached yet. After the last one the check
 * is left to Retry, which asks the server before it sends anything (see retryLastTurn).
 */
export const RECONCILE_RETRY_DELAYS_MS: readonly number[] = [10000, 20000]

/**
 * The longest streamed text waits to be shown when no animation frame comes (a tab in the
 * background gets none): the answer still completes there.
 */
export const FRAME_FALLBACK_MS = 100

/**
 * Runs `run` once, at the next animation frame or after FRAME_FALLBACK_MS, whichever comes
 * first. Returns what cancels it.
 */
const onNextFrame = (run: () => void): (() => void) => {
  let frame: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (frame !== null) {
      cancelAnimationFrame(frame)
      frame = null
    }
  }
  const fire = () => {
    cancel()
    run()
  }
  timer = setTimeout(fire, FRAME_FALLBACK_MS)
  if (typeof requestAnimationFrame === 'function') {
    frame = requestAnimationFrame(fire)
  }
  return cancel
}

/** A question handed back to the composer: its text and the records tagged in it. */
interface DraftSnapshot {
  text: string
  mentions: AssistantDraftMention[]
}

/** What a turn sent, kept so the same question can be sent again (see retryLastTurn). */
interface SentTurn {
  conversationId: string
  content: string
  clarificationResponse?: {
    messageId: string
    selectedValue?: string
    freeText?: string
  }
  command?: string
  mentions?: { entityType: string; id: string; label: string }[]
  /** The record that was open when the question was asked: a retry asks about the same one. */
  pageContext: AssistantPageContext | null
  /** The question as it is on screen: a retry adds no second bubble for it. */
  question: AssistantChatMessage
  /** Where the question stood when the turn was sent (see reconcileTurn). */
  place: QuestionPlace
  /**
   * A typed answer to a clarification that was handed back to the composer because nothing had
   * come back for it; taken out of the composer again if the server turns out to have stored it.
   */
  handedBack?: DraftSnapshot
}

/** What one load of a conversation says about the messages before the ones it returned. */
interface MessagePaging {
  hasEarlier: boolean
  /** The server's cursor for the page before, sent back exactly as received. */
  earlierBefore: string | null
}

/**
 * `draft` without a text that was handed back to the composer (see takeBackHandedDraft): only
 * while it is still there as it was handed back, alone or followed on a new line by what was
 * typed after it. Null when it is not (the user changed it): the draft is then left as it is.
 */
const withoutHandedBack = (draft: string, handed: string): string | null => {
  if (draft === handed) {
    return ''
  }
  if (draft.startsWith(`${handed}\n`)) {
    return draft.slice(handed.length + 1)
  }
  return null
}

const pagingOf = (data: any): MessagePaging => {
  const earlierBefore =
    typeof data?.earlierBefore === 'string' && data.earlierBefore
      ? data.earlierBefore
      : null
  return {
    hasEarlier: data?.hasEarlier === true && earlierBefore !== null,
    earlierBefore,
  }
}

/**
 * Where a session finds the record its questions are about:
 *  - screen: the record on screen, as the drawer does (the active dashboard tab, or the snapshot
 *    the standalone page was opened with); it follows the screen;
 *  - host: the record the embedding component passes in (a placement such as the Case history
 *    pane); it never follows the dashboard tab.
 */
export type AssistantSessionContextSource =
  | { kind: 'screen' }
  | { kind: 'host'; context: AssistantPageContext | null }

/**
 * One chat shown by one view of the Assistant: the drawer (and the standalone page) has one, and
 * every embedded placement has one per record (see AssistantStore.sessionFor). It holds what
 * belongs to that conversation only (its messages, the running turn, the draft, the errors, the
 * Guide/History views, the record it asks about); what the user has app-wide (capabilities, the
 * privacy notice, the conversation list, the guide pages, the briefing, which view is shown) is
 * read from the store.
 */
export class AssistantSession {
  /** `drawer`, or `<placement>:<record>` for a placement's session. */
  readonly key: string
  /**
   * The placement this chat belongs to (see AssistantStore.sessionFor); null for the drawer's
   * chat, which sends no placement and creates its conversations without an origin.
   */
  readonly placement: AssistantPlacement | null
  /** Where the app-wide state (capabilities, conversation list, guide pages) is read. */
  private readonly store: AssistantStore
  /** Where the record the questions are about comes from (see AssistantSessionContextSource). */
  readonly contextSource: 'screen' | 'host'
  /** With contextSource 'host': the record the embedding component passed in. */
  hostContext: AssistantPageContext | null = null

  showHistory = false
  /**
   * Counts the times the History was left for the chat by going back (its "Back to chat",
   * Escape, the History button again) in a placement with a History of its own: the panel then
   * puts the focus back on the History button.
   */
  historyReturnFocus = 0
  /**
   * The History of a placement whose list is its own (historyScope 'placement'): only this
   * placement's conversations about its record. Null for the drawer and for a placement that
   * shows the drawer's list.
   */
  readonly placementHistory: AssistantPlacementHistory | null
  /** The Guide panel (user guide per skill) replaces the chat while open. */
  showGuide = false
  /** Guide page being read. */
  guideKey: string | null = null
  /** A section of that page to scroll to once it is shown (then cleared). */
  guideSection: string | null = null
  activeConversationId: string | null = null
  messages: AssistantChatMessage[] = []
  /** The open conversation has messages older than the loaded ones (see loadEarlierMessages). */
  hasEarlierMessages = false
  loadingEarlierMessages = false
  /**
   * Phase of the running turn. A failed turn is back to 'idle' with an `errorCode` — there is
   * no error phase, so nothing has to reset it before the next turn or a new chat.
   */
  status: 'idle' | 'sending' | 'streaming' = 'idle'
  /** The conversation picked from the history that is being fetched; null while none is. */
  loadingConversationId: string | null = null
  /** The turn currently streaming in (see assistantTurn.ts). */
  turn: TurnState = initialTurn()
  errorCode: string | null = null
  /** Mentions tagged in the current composer draft; validated against the text on send. */
  draftMentions: AssistantDraftMention[] = []
  /**
   * What is typed in the composer. Kept here, not in the Composer, so it is still there after
   * the Guide or the History was opened over the chat, and so it always matches draftMentions.
   */
  draftText = ''
  /**
   * Counts the times the draft was put in place by something other than typing (a prefill, an
   * example from the guide, a question handed back): the Composer then moves the caret to its
   * end.
   */
  draftRevision = 0
  /**
   * Counts the times something asked for the cursor to be put in the composer (a control of the
   * panel that removed itself when it was used, and would otherwise leave the focus nowhere).
   */
  composerFocusRequest = 0
  /**
   * The standalone page (its own browser tab) has no screen of the app behind it: its context is
   * the record that was open when it was opened, a snapshot that never follows the app tab.
   */
  isStandalone = false
  /** That snapshot; null when the page was opened without a record, or the chip was removed. */
  standaloneContext: AssistantPageContext | null = null
  /** Action whose confirm/cancel call is in flight (disables its buttons). */
  actionInFlight: string | null = null

  /** The record whose Context chip the user removed (see dismissPageContext). */
  private dismissedContextKey: string | null = null
  /** The record the server answered it would not use as context (see applyContextVerdict). */
  private refusedContextKey: string | null = null
  /** The record the server last accepted as context, under the record's own number or name. */
  private confirmedContext: { key: string; label: string } | null = null

  /** The cursor of the page before the loaded messages; null when there is none. */
  private earlierBefore: string | null = null
  /** What each fetched message list said about earlier messages (see adoptPaging). */
  private fetchedPaging = new WeakMap<AssistantChatMessage[], MessagePaging>()
  private abortController: AbortController | null = null
  /**
   * Identifies the newest request for the message list. A conversation load applies its
   * result only while its token is still the newest; a newer load, a new chat, a turn or
   * Stop all move it on.
   */
  private loadToken = 0
  /** The question of a turn that has not reached the server yet (see restoreUnsentDraft). */
  private unsentDraft: DraftSnapshot | null = null
  /**
   * The clarification the running turn answers, until the server shows it took the answer
   * (see reopenClarification).
   */
  private answeringClarification: {
    prompt: AssistantChatMessage
    /** The user's answer as pushed to the message list. */
    answer: AssistantChatMessage | null
    /** A typed answer goes back to the composer; a picked option has nothing to give back. */
    draft: DraftSnapshot | null
  } | null = null
  /**
   * A conversation the server created for a first message that was stopped before it was sent:
   * it is empty, so the next first message uses it instead of creating another one.
   */
  private spareConversationId: string | null = null
  /** The turn sent last, while it has not been answered (see canRetry). */
  private lastTurn: SentTurn | null = null
  /** What the server said with the error of the last failed turn (see errorMessage). */
  private serverError: { code: string; message: string } | null = null
  /** The wait before a cut-off turn is checked against the server (see reconcileTurn). */
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Events of the running turn that arrived since the last animation frame and are not in
   * `turn` yet (see queueTurnEvent). Nothing renders from these two: they are not observable.
   */
  private pendingEvents: TurnEvent[] = []
  /** Cancels the frame that will apply pendingEvents; null while none is waiting. */
  private cancelFrame: (() => void) | null = null
  /** Stops following the screen (see the constructor); null for a host session. */
  private disposeContextReaction: IReactionDisposer | null = null
  /** The placement's last conversation about its record was asked for (see resumeLatest). */
  private resumeRequested = false
  /** Counts what the user typed in the composer: a resume that finds it moved on gives way. */
  private draftEdits = 0

  constructor(
    store: AssistantStore,
    key: string,
    source: AssistantSessionContextSource = { kind: 'screen' },
    placement: AssistantPlacement | null = null
  ) {
    this.store = store
    this.key = key
    this.placement = placement
    this.contextSource = source.kind
    if (source.kind === 'host') {
      this.hostContext = source.context
    }
    this.placementHistory =
      placement?.historyScope === 'placement' &&
      placement.conversationScope === 'placement'
        ? new AssistantPlacementHistory(() => {
            const subject = this.placementSubject()
            return subject ? { placement: placement.id, ...subject } : null
          }, store)
        : null
    makeAutoObservable<
      AssistantSession,
      | 'store'
      | 'pendingEvents'
      | 'cancelFrame'
      | 'screenContext'
      | 'fetchedPaging'
      | 'disposeContextReaction'
    >(this, {
      store: false,
      key: false,
      // Static config (with a function in it): never made observable.
      placement: false,
      // Observable on its own.
      placementHistory: false,
      contextSource: false,
      pendingEvents: false,
      cancelFrame: false,
      fetchedPaging: false,
      disposeContextReaction: false,
      // A plain read of the dashboard: whoever calls it tracks (or does not track) the tab.
      screenContext: false,
    })
    if (source.kind === 'screen') {
      // Another record on screen is another context: a chip removed for the record before, or a
      // record the server would not use, says nothing about this one. The same holds for coming
      // back to that record later. A host session's record never changes, so it has no reaction.
      this.disposeContextReaction = reaction(
        () => pageContextKey(this.screenContext()),
        () => {
          this.dismissedContextKey = null
          this.refusedContextKey = null
        }
      )
    }
  }

  get streamingContent() {
    return this.turn.content
  }

  /** Progress label of the running tool, e.g. "Searching cases…". */
  get toolActivity() {
    return this.turn.status
  }

  /** A turn is being sent or is streaming in. */
  get isTurnRunning() {
    return this.status !== 'idle'
  }

  /** A conversation picked from the history is being fetched. */
  get loadingConversation() {
    return this.loadingConversationId !== null
  }

  /** A turn is running or a conversation is loading. */
  get isBusy() {
    return this.isTurnRunning || this.loadingConversation
  }

  /**
   * Nothing new can be asked: the assistant is not available to this user, it is busy, or a
   * confirm/cancel is being applied (its follow-up is the next message of this conversation, so
   * a question must not get in before it). The next question can still be typed while it is
   * only busy (see Composer). Nothing is asked either before the privacy notice is closed.
   */
  get isInputLocked() {
    return (
      this.store.unavailableReason !== null ||
      this.store.privacyNotice !== null ||
      this.isBusy ||
      this.actionInFlight !== null
    )
  }

  /**
   * The server's own wording for the current error, which says more than the generic text of
   * its code (AI_BUSY: "Try again in about N seconds"); null when it sent none.
   */
  get errorMessage(): string | null {
    return this.errorCode !== null && this.serverError?.code === this.errorCode
      ? this.serverError.message
      : null
  }

  /**
   * The failed turn can be sent again as it was: its error is one a retry can get past, and its
   * question is still on screen in the open conversation. An answer to a clarification can be
   * sent again after any error of the turn engine: the card is answered, so there is no other
   * way to give the same answer.
   */
  get canRetry() {
    const turn = this.lastTurn
    return (
      turn !== null &&
      this.errorCode !== null &&
      (RETRYABLE_ERROR_CODES.has(this.errorCode) ||
        (turn.clarificationResponse !== undefined &&
          TURN_ENGINE_ERROR_CODES.has(this.errorCode))) &&
      !this.isInputLocked &&
      turn.conversationId === this.activeConversationId &&
      this.messages.includes(turn.question)
    )
  }

  get pendingClarification(): AssistantChatMessage | null {
    const last = this.messages[this.messages.length - 1]
    return last?.clarification && !last.clarificationAnswered ? last : null
  }

  /**
   * The record the questions of this session are about, whatever the Assistant can do with it:
   * for a host session the one the embedding component passed in; otherwise the active
   * dashboard tab's, or on the standalone page the one it was opened with.
   */
  private screenContext(): AssistantPageContext | null {
    if (this.contextSource === 'host') {
      return this.hostContext
    }
    if (this.isStandalone) {
      return this.standaloneContext
    }
    const tab = dashboardStore.currentTab
    return pageContextFromPath(tab?.path, tab?.title)
  }

  /**
   * The record a question is asked about when it says "this case" / "this report": the one on
   * screen, unless the user removed its chip, the server answered it does not use it, or the
   * tool that reads that kind of record is not the user's. It is what the Context chip shows and
   * what is sent with the next question. Once the server has accepted the record, the label is
   * the server's (the record's own number or name).
   */
  get activePageContext(): AssistantPageContext | null {
    const context = this.screenContext()
    const key = pageContextKey(context)
    if (
      !context ||
      key === this.dismissedContextKey ||
      key === this.refusedContextKey ||
      !(context.list
        ? this.canUseListContext(context)
        : this.canUseContextOf(context.type))
    ) {
      return null
    }
    return this.confirmedContext?.key === key
      ? { ...context, label: this.confirmedContext.label }
      : context
  }

  /**
   * The record the panel itself sits on, whether or not it is used as context: the one the
   * embedding component passed in for a host session, else the active page context. A
   * placement names it in its header and hides the links that lead back to it (hidesLink).
   */
  get placementContext(): AssistantPageContext | null {
    return this.contextSource === 'host'
      ? this.hostContext
      : this.activePageContext
  }

  /**
   * True when the server answered that the record on screen is not used as context (it is not
   * available to this user): said under the composer instead of a chip that names the record.
   */
  get pageContextRefused() {
    const key = pageContextKey(this.screenContext())
    return key !== null && key === this.refusedContextKey
  }

  /** With pageContextRefused: what the server does not use is a list screen, not a record. */
  get pageContextRefusedIsList() {
    return this.pageContextRefused && !!this.screenContext()?.list
  }

  /**
   * Whether the Assistant can search, for this user, the list on screen: its search tool is
   * theirs and, for a case list, that case type is on (the server lists the case type's own
   * command only then). The server decides in the end; this keeps a chip from promising a list
   * the answer would not use.
   */
  private canUseListContext = (context: AssistantPageContext) => {
    const toolName = pageContextListTool(context)
    if (!toolName || !this.store.canUseTool(toolName)) {
      return false
    }
    const serviceType = context.list?.serviceType
    return (
      !serviceType ||
      !this.store.capabilitiesKnown ||
      this.store.commands.some(c => c.name === caseListCommand(serviceType))
    )
  }

  /** Whether the Assistant has, for this user, the tool that reads records of this kind. */
  private canUseContextOf = (type: string) => {
    const toolName = pageContextTool(type)
    if (toolName) {
      return this.store.canUseTool(toolName)
    }
    // A kind without a tool of its own is vouched for like an @-mention of that kind.
    return (
      !this.store.capabilitiesKnown ||
      this.store.mentionTypes.some(t => t.type === type)
    )
  }

  /** The user removed the Context chip: the record is not sent until another one is on screen. */
  dismissPageContext = () => {
    if (this.contextSource === 'screen' && this.isStandalone) {
      this.standaloneContext = null
      return
    }
    this.dismissedContextKey = pageContextKey(this.screenContext())
  }

  /**
   * The standalone page tells the store it runs there, and with which record it was opened
   * (null: none). From then on the dashboard tab of this browser tab is never read.
   */
  enterStandalone = (context: AssistantPageContext | null) => {
    this.isStandalone = true
    this.standaloneContext = context
  }

  /**
   * A host session's record, as the embedding component has it now (its label may arrive after
   * the session was created). Only the label can change: the session belongs to the record.
   */
  setHostContext = (context: AssistantPageContext | null) => {
    if (this.contextSource !== 'host') {
      return
    }
    this.hostContext = context
  }

  /**
   * What the server made of the context sent with a question (the `context` event): the record
   * under its own number or name, or that it is not used. The chip follows the server, so it
   * never names a record the answer was not about.
   */
  private applyContextVerdict = (
    sent: AssistantPageContext | null,
    verdict: { used?: boolean; label?: string | null }
  ) => {
    const key = pageContextKey(sent)
    if (!key) {
      return
    }
    if (verdict.used) {
      const label = typeof verdict.label === 'string' ? verdict.label : ''
      this.confirmedContext = label ? { key, label } : null
      if (this.refusedContextKey === key) {
        this.refusedContextKey = null
      }
    } else {
      this.refusedContextKey = key
      if (this.confirmedContext?.key === key) {
        this.confirmedContext = null
      }
    }
  }

  /** Puts the cursor in the composer (see composerFocusRequest). */
  requestComposerFocus = () => {
    this.composerFocusRequest++
  }

  applySuggestion = (suggestion: AssistantSuggestedQuery) => {
    if (suggestion.prefillOnly) {
      this.setDraft(suggestion.text)
    } else {
      this.sendMessage(suggestion.text)
    }
  }

  /** The user typed in the composer. */
  editDraft = (text: string) => {
    this.draftText = text
    this.draftEdits++
  }

  /**
   * Puts a text in the composer in place of what is there (a prefill-only suggestion, an
   * example from the guide), with the caret at its end.
   */
  setDraft = (text: string) => {
    this.draftText = text
    this.draftRevision++
  }

  /**
   * Hands a question back to the composer with its tagged records. The next question may have
   * been typed since it was sent (typing is not locked while a turn runs): that text is kept,
   * after the one handed back.
   */
  private giveBackDraft = (draft: DraftSnapshot) => {
    const typed = this.draftText
    if (typed.trim()) {
      this.draftText = `${draft.text}\n${typed}`
      this.draftMentions = [...draft.mentions, ...this.draftMentions]
    } else {
      this.draftText = draft.text
      this.draftMentions = draft.mentions
    }
    this.draftRevision++
  }

  /**
   * What is typed in the composer, less an answer to a clarification that was handed back to it
   * while the server may still turn out to have stored it (see takeBackHandedDraft): the text
   * that can move to another session ("Open in full K-Assist") without being asked twice.
   */
  draftWithoutHandedBack = (): string => {
    const handed = this.lastTurn?.handedBack
    if (!handed) {
      return this.draftText
    }
    return withoutHandedBack(this.draftText, handed.text) ?? this.draftText
  }

  /** Closes the warning shown under the messages / above the history. */
  dismissError = () => {
    this.errorCode = null
  }

  registerDraftMention = (mention: AssistantDraftMention) => {
    this.draftMentions.push(mention)
  }

  newConversation = () => {
    if (this.isTurnRunning) {
      return
    }
    this.cancelConversationLoad()
    this.activeConversationId = null
    this.lastTurn = null
    this.messages = []
    this.adoptPaging(this.messages)
    this.errorCode = null
    this.draftText = ''
    this.draftMentions = []
    this.showHistory = false
    this.showGuide = false
  }

  /**
   * Another session has opened this session's conversation (the drawer picked it from the
   * History, or took it over with "Open in full K-Assist"): this one is back to an empty chat, so
   * no two views show the same conversation. What is typed in the composer stays. Never while it
   * is answering in it (see AssistantStore.isConversationBusyElsewhere).
   */
  letGoOfConversation = () => {
    if (this.isTurnRunning || this.actionInFlight !== null) {
      return
    }
    this.cancelConversationLoad()
    this.activeConversationId = null
    this.lastTurn = null
    this.messages = []
    this.adoptPaging(this.messages)
    this.errorCode = null
  }

  /**
   * Moves the Assistant to its own page in a new browser tab, continuing this conversation;
   * its links keep navigating this tab.
   */
  openInNewWindow = () => {
    // The record on screen goes along as a snapshot: the new tab has no screen of its own, and
    // "this case" must still mean the case the user was looking at.
    const params = new URLSearchParams({
      ...(this.activeConversationId
        ? { conversationId: this.activeConversationId }
        : {}),
      ...pageContextToQuery(this.activePageContext),
    }).toString()
    const query = params ? `?${params}` : ''
    if (window.open(`${ASSISTANT_PAGE_PATH}${query}`, '_blank')) {
      this.store.close()
      this.store.closeEmbedded()
    }
  }

  toggleHistory = () => {
    this.showHistory = !this.showHistory
    this.showGuide = false
    // A failed pick is reported in the history; it says nothing about the chat behind it.
    if (this.errorCode === 'CONVERSATION_LOAD_FAILED') {
      this.errorCode = null
    }
    // A History of the placement's own is read when it is opened (each time: the drawer may
    // have gone on with one of its conversations meanwhile), never before.
    if (this.showHistory) {
      this.placementHistory?.load()
    }
  }

  /**
   * Back from the History to the chat (its "Back to chat", Escape, the History button again):
   * in a placement with a History of its own, the focus then goes back to the History button.
   */
  leaveHistory = () => {
    if (!this.showHistory) {
      return
    }
    this.toggleHistory()
    if (this.placementHistory) {
      this.historyReturnFocus++
    }
  }

  /**
   * Opens the Guide panel, on `key` when given (else the page read last / the first page),
   * scrolled to `section` when given.
   */
  openGuide = (key?: string, section?: string) => {
    this.showGuide = true
    this.showHistory = false
    if (key) {
      this.guideKey = key
    }
    this.guideSection = section ?? null
    this.loadDocs()
  }

  toggleGuide = () => {
    if (this.showGuide) {
      this.showGuide = false
    } else {
      this.openGuide()
    }
  }

  /**
   * Reads the guide's pages (once per page load, shared by every session) and, when this
   * session has no page open yet, opens the first one.
   */
  loadDocs = async () => {
    await this.store.fetchDocs()
    runInAction(() => {
      if (!this.guideKey && this.store.docs.length) {
        this.guideKey = this.store.docs[0].key
      }
    })
  }

  /** An example prompt from the guide: back to the chat with it in the composer (not sent). */
  tryPrompt = (text: string) => {
    this.setDraft(text)
    this.showGuide = false
  }

  /**
   * Opens a conversation from the history. Picking another one while this one is still
   * loading is allowed: only the newest pick is shown, whatever order the responses arrive in.
   * Not one another session is still answering in (its row says "Answering…"); one another
   * session merely shows is taken from it once loaded (one conversation, one live session).
   */
  selectConversation = (id: string) => this.openConversation(id)

  /**
   * Opens conversation `id` (see selectConversation). `silent`: a load the user did not ask for
   * (see resumeLatest); when it fails the chat stays empty and nothing is said.
   */
  private openConversation = async (id: string, silent = false) => {
    if (
      this.isTurnRunning ||
      this.store.isConversationBusyElsewhere(id, this)
    ) {
      return
    }
    const token = ++this.loadToken
    this.loadingConversationId = id
    if (this.errorCode === 'CONVERSATION_LOAD_FAILED') {
      this.errorCode = null
    }
    // Superseded by a newer load, a new chat, a turn or Stop: this result must not land.
    const isStale = () => token !== this.loadToken || this.isTurnRunning
    try {
      const messages = await this.fetchMessages(id, silent)
      runInAction(() => {
        if (isStale()) {
          return
        }
        // A load the user did not ask for never takes a conversation another view shows or is
        // opening now (it stays there); a pick of the user's own does.
        if (silent && this.isConversationHeldElsewhere(id)) {
          this.loadingConversationId = null
          return
        }
        this.loadingConversationId = null
        this.activeConversationId = id
        this.errorCode = null
        // Only a pick of the user's closes the History: a resume they did not ask for loads
        // behind it while they read the list (the row then shows as the open one).
        if (!silent) {
          this.showHistory = false
        }
        this.messages = messages
        this.adoptPaging(messages)
        this.store.conversationOpened(this, id)
      })
    } catch {
      runInAction(() => {
        if (isStale()) {
          return
        }
        this.loadingConversationId = null
        // Shown where the user is: above the history (ConversationList) or under the messages.
        if (!silent) {
          this.errorCode = 'CONVERSATION_LOAD_FAILED'
        }
      })
    }
  }

  /** Another session shows conversation `id`, or is opening it now. */
  private isConversationHeldElsewhere = (id: string) =>
    this.store.allSessions.some(
      session =>
        session !== this &&
        (session.activeConversationId === id ||
          session.loadingConversationId === id)
    )

  /**
   * The record a placement's conversations are about, as it is stored on them (their subject):
   * the record the embedding component passed in, when it is one the server could take (not a
   * list, an id in the shape of the server's). Null for the drawer.
   */
  private placementSubject = (): {
    subjectType: string
    subjectId: string
  } | null => {
    const context = this.placement ? this.hostContext : null
    if (!context || context.list || !isRecordId(context.id)) {
      return null
    }
    return { subjectType: context.type, subjectId: context.id }
  }

  /**
   * What a conversation this session creates is stored with: the placement it was started from
   * and the record that placement sits on. Nothing for the drawer, whose request stays as it was.
   */
  private conversationOrigin = ():
    | CreateAssistantConversationBody
    | undefined => {
    if (!this.placement) {
      return undefined
    }
    return { placement: this.placement.id, ...this.placementSubject() }
  }

  /**
   * The key sent with every turn of a placement that asks the server for its focus (the server
   * narrows what it offers to the placement's own allow-list). None for the drawer, nor for a
   * placement without serverFocus: their requests stay as they were.
   */
  private get serverPlacement(): string | undefined {
    return this.placement?.serverFocus ? this.placement.id : undefined
  }

  /**
   * A placement with its own conversation per record is shown for the first time: the newest
   * conversation the user had with it about this record is asked for and opened, so the chat
   * goes on after a reload. Once per session, and only while the chat is still untouched: a
   * question, a new chat, another conversation or anything typed in the meantime wins, and so
   * does a conversation another view holds now (it stays there). Silent: when nothing is found
   * or the server cannot be asked, the chat stays empty.
   */
  resumeLatest = async () => {
    const placement = this.placement
    const subject = this.placementSubject()
    if (
      this.resumeRequested ||
      !placement ||
      placement.conversationScope !== 'placement' ||
      !subject
    ) {
      return
    }
    this.resumeRequested = true
    const untouched = () =>
      this.activeConversationId === null &&
      this.messages.length === 0 &&
      this.spareConversationId === null &&
      !this.isBusy &&
      this.actionInFlight === null
    if (!untouched()) {
      return
    }
    const token = this.loadToken
    const edits = this.draftEdits
    const revision = this.draftRevision
    let id: string | null = null
    try {
      const res = await ApiService.Assistant.GetLatestConversation({
        placement: placement.id,
        ...subject,
      })
      // None found: an empty answer (204).
      const found = res?.data?.id
      id = typeof found === 'string' && found ? found : null
    } catch {
      // Nothing to go on with: the chat stays empty.
      return
    }
    if (
      !id ||
      token !== this.loadToken ||
      edits !== this.draftEdits ||
      revision !== this.draftRevision ||
      !untouched() ||
      this.isConversationHeldElsewhere(id)
    ) {
      return
    }
    await this.openConversation(id, true)
  }

  /** A conversation's messages as the server has them, in the shape the list renders. */
  private fetchMessages = async (
    id: string,
    /** A read the user did not ask for: its failure is not reported to them. */
    silent = false
  ): Promise<AssistantChatMessage[]> => {
    const res = silent
      ? await ApiService.Assistant.GetConversation(id, { silent: true })
      : await ApiService.Assistant.GetConversation(id)
    const messages: AssistantChatMessage[] = (res.data?.messages ?? [])
      .filter((m: any) => m.role === 'User' || m.role === 'Assistant')
      .map((m: any, index: number, all: any[]) =>
        toChatMessage(m, index === all.length - 1)
      )
    this.fetchedPaging.set(messages, pagingOf(res.data))
    return messages
  }

  /**
   * Whoever puts a fetched list on screen in place of the messages also takes over what that
   * fetch said about earlier messages: the server returns the newest ones, so earlier pages that
   * were loaded before are no longer on screen, and "Load earlier messages" starts from this list.
   */
  private adoptPaging = (messages: AssistantChatMessage[]) => {
    const paging = this.fetchedPaging.get(messages)
    this.hasEarlierMessages = paging?.hasEarlier === true
    this.earlierBefore = paging?.earlierBefore ?? null
    this.loadingEarlierMessages = false
  }

  /**
   * Loads the page of messages before the ones on screen and puts it above them. Not while a
   * turn runs or another conversation is being opened; whatever replaces the messages meanwhile
   * (another conversation, a new chat, a turn) drops the result.
   */
  loadEarlierMessages = async () => {
    const conversationId = this.activeConversationId
    const before = this.earlierBefore
    if (
      !conversationId ||
      !before ||
      !this.hasEarlierMessages ||
      this.loadingEarlierMessages ||
      this.isBusy
    ) {
      return
    }
    const token = this.loadToken
    const first = this.messages[0]
    this.loadingEarlierMessages = true
    try {
      const res = await ApiService.Assistant.GetConversation(conversationId, {
        before,
      })
      runInAction(() => {
        if (
          token !== this.loadToken ||
          this.activeConversationId !== conversationId ||
          this.earlierBefore !== before ||
          this.messages[0] !== first
        ) {
          return
        }
        const known = new Set(this.messages.map(m => m.id))
        const earlier: AssistantChatMessage[] = (res.data?.messages ?? [])
          .filter((m: any) => m.role === 'User' || m.role === 'Assistant')
          .map((m: any) => toChatMessage(m))
          .filter((m: AssistantChatMessage) => !m.id || !known.has(m.id))
        this.messages = [...earlier, ...this.messages]
        const paging = pagingOf(res.data)
        this.hasEarlierMessages = paging.hasEarlier
        this.earlierBefore = paging.earlierBefore
      })
    } catch {
      // The button stays and can be pressed again.
    } finally {
      runInAction(() => {
        this.loadingEarlierMessages = false
      })
    }
  }

  /**
   * Brings the open conversation back in line with the server after a confirm/cancel whose
   * result did not arrive: the server has the card's real state and, if the action ran, its
   * follow-up message. Unlike picking a conversation from the history it changes nothing but
   * the messages, and only while `isStale` says they are still the ones the decision was made
   * on. A reload that fails leaves the screen as it is.
   */
  private reconcileMessages = async (
    conversationId: string,
    isStale: () => boolean
  ) => {
    try {
      const messages = await this.fetchMessages(conversationId)
      runInAction(() => {
        if (!isStale()) {
          this.messages = messages
          this.adoptPaging(messages)
        }
      })
    } catch {
      // Nothing better to show than what is on screen; the warning (if any) already says so.
    }
  }

  /**
   * Drops a conversation load that is still in flight; its response is ignored. So is the
   * check of a cut-off turn against the server, whether it is waiting or already reading.
   */
  private cancelConversationLoad = () => {
    this.loadToken++
    this.loadingConversationId = null
    if (this.reconcileTimer !== null) {
      clearTimeout(this.reconcileTimer)
      this.reconcileTimer = null
    }
  }

  /**
   * A turn was cut off without the server saying how it ended (the connection dropped, the
   * stream went silent, the server restarted): the server may have kept more of it than
   * arrived here. After a moment the conversation is read again, and only when the server
   * holds this very turn is the screen brought in line with it:
   *  - it holds the finished answer: the answer is shown, the warning and Retry go — sending
   *    the question again would only ask it twice;
   *  - it holds an answer that was cut short: that one replaces what arrived here (it can be
   *    rated), and Retry stays;
   *  - it holds the answer to a clarification that was opened again here because nothing had
   *    come back: the answer is shown as given, and Retry sends it again instead of a second
   *    answer being picked.
   * In every other case the screen stays as it is: that the server shows nothing of the turn
   * yet does not mean it has nothing. A read that fails (the server is often still out of reach
   * right after the cut) is made again, a few times; and whatever this check did not settle,
   * Retry asks the server once more before it sends (see retryLastTurn). Anything the user does
   * meanwhile (another turn, Stop, another conversation, a confirm/cancel) drops the check.
   */
  private reconcileTurn = async (turn: SentTurn) => {
    const token = this.loadToken
    const isStale = () =>
      token !== this.loadToken ||
      this.isTurnRunning ||
      this.actionInFlight !== null ||
      this.lastTurn !== turn

    let stored: AssistantChatMessage[] | null = null
    for (const wait of [RECONCILE_DELAY_MS, ...RECONCILE_RETRY_DELAYS_MS]) {
      await new Promise<void>(resolve => {
        this.reconcileTimer = setTimeout(resolve, wait)
      })
      if (isStale()) {
        return
      }
      runInAction(() => {
        this.reconcileTimer = null
      })
      try {
        stored = await this.fetchMessages(turn.conversationId, true)
        break
      } catch {
        // Not reachable yet: asked again after the next wait.
        if (isStale()) {
          return
        }
      }
    }
    if (!stored) {
      // Nothing better to show than what is on screen.
      return
    }

    const messages = stored
    runInAction(() => {
      if (!isStale()) {
        this.applyStoredTurn(turn, messages)
      }
    })
  }

  /**
   * Brings the screen in line with the server's copy of the conversation when that copy holds
   * this very turn (see reconcileTurn for the cases). Returns whether the turn turned out to be
   * answered in full: it is over then, with nothing left to retry.
   */
  private applyStoredTurn = (
    turn: SentTurn,
    stored: AssistantChatMessage[]
  ): boolean => {
    const found = findStoredTurn(stored, turn.place)
    if (!found) {
      return false
    }
    if (!found.answer && this.messages.includes(turn.question)) {
      return false
    }
    this.messages = stored
    this.adoptPaging(stored)
    // The question on screen is now the server's copy of it: Retry sends that one again.
    turn.question =
      this.messages.find(m => m.id === found.question.id) ?? turn.question
    turn.place = placeOfQuestion(this.messages, turn.question)
    this.takeBackHandedDraft(turn)
    if (found.answer && !found.answer.partial) {
      this.lastTurn = null
      this.errorCode = null
      this.serverError = null
      this.store.refreshConversationsAfterTurn(this) // refresh titles / ordering
      return true
    }
    return false
  }

  /**
   * The server holds an answer that was handed back to the composer (see reopenClarification):
   * it is on screen as given now, so it leaves the composer — sent from there it would be a
   * second, unrelated question. Only while it is still there as it was handed back; what the
   * user typed after it stays.
   */
  private takeBackHandedDraft = (turn: SentTurn) => {
    const handed = turn.handedBack
    turn.handedBack = undefined
    if (!handed) {
      return
    }
    const kept = withoutHandedBack(this.draftText, handed.text)
    if (kept === null) {
      return
    }
    this.draftText = kept
    this.draftMentions = this.draftMentions.filter(m =>
      this.draftText.includes(m.display)
    )
    this.draftRevision++
  }

  /**
   * The conversation `id` was deleted: an empty one kept for the next first message is not used,
   * and when it is the open one its answer is ended and the chat starts over.
   */
  conversationDeleted = (id: string) => {
    if (this.spareConversationId === id) {
      this.spareConversationId = null
    }
    if (this.activeConversationId === id) {
      // Its answer may still be coming in: end that turn first, or the reset is refused and
      // the next question would go to a conversation that no longer exists.
      this.stop()
      this.newConversation()
    }
  }

  /**
   * Sends a question. When a clarification is pending, the text answers it.
   * `fromComposer`: the text is the composer's draft. The composer is emptied, and the text is
   * handed back there if it never reaches the server. A suggestion or briefing prompt is not:
   * whatever the user has typed in the composer stays as it is.
   * `commands: false`: the placement has no slash commands, so a leading "/name" is not read as
   * one here and goes as plain text (the server forces a tool only when the placement offers it).
   */
  sendMessage = async (
    content: string,
    {
      fromComposer = false,
      commands = true,
    }: { fromComposer?: boolean; commands?: boolean } = {}
  ) => {
    const text = content.trim()
    if (!text || this.isInputLocked) {
      return
    }

    // Mentions still present in the text (the user may have edited them away).
    // Kept as tagged so they can go back to the composer if the turn never reaches the server.
    const draftMentions = this.draftMentions.filter(m =>
      text.includes(m.display)
    )
    const mentions = draftMentions
      .slice(0, MAX_MENTIONS_PER_MESSAGE)
      .map(m => ({
        entityType: m.entityType,
        id: m.id,
        label: m.label,
      }))
    const draft = fromComposer ? { text, mentions: draftMentions } : null
    if (fromComposer) {
      this.draftText = ''
      this.draftMentions = []
    }

    const pending = this.pendingClarification
    if (pending?.id && pending.clarification?.allowFreeText !== false) {
      pending.clarificationAnswered = true
      this.answeringClarification = { prompt: pending, answer: null, draft }
      await this.dispatchTurn(
        text,
        { messageId: pending.id, freeText: text },
        undefined,
        mentions
      )
      return
    }

    await this.dispatchTurn(
      text,
      undefined,
      commands ? this.detectCommand(text) : undefined,
      mentions,
      draft
    )
  }

  /** "/ime-search ..." → "ime-search" when it matches a registered command. */
  private detectCommand = (text: string): string | undefined => {
    const match = /^\/([a-z0-9-]+)(\s|$)/i.exec(text)
    if (!match) {
      return undefined
    }
    return this.store.commands.find(
      c => c.name.toLowerCase() === match[1].toLowerCase()
    )?.name
  }

  /** User picked one of the suggested clarification options. */
  answerClarification = async (option: AssistantClarificationOption) => {
    const pending = this.pendingClarification
    if (!pending?.id || this.isInputLocked) {
      return
    }
    pending.clarificationAnswered = true
    pending.clarificationSelectedValue = option.value
    this.answeringClarification = { prompt: pending, answer: null, draft: null }
    await this.dispatchTurn(option.label, {
      messageId: pending.id,
      selectedValue: option.value,
    })
  }

  /**
   * Sends the failed turn again, as it was sent. The question is already on screen and, when
   * it reached the server, on record there: no second bubble is added and the server is told it
   * is a retry, so it does not store the question twice.
   */
  retryLastTurn = async () => {
    const turn = this.lastTurn
    if (!turn || !this.canRetry) {
      return
    }
    if (
      this.errorCode !== null &&
      RECONCILED_ERROR_CODES.has(this.errorCode) &&
      !(await this.isStillUnanswered(turn))
    ) {
      return
    }
    await this.dispatchTurn(
      turn.content,
      turn.clarificationResponse,
      turn.command,
      turn.mentions,
      null,
      turn.question,
      turn.pageContext
    )
  }

  /**
   * Before a turn that was cut off is sent again, the server is asked what it kept of it: the
   * check after the cut may have failed (still offline) or come too early, and a question the
   * server has answered would otherwise be stored and answered a second time. Returns false when
   * there is nothing to send any more: the answer was there and is on screen now, or the user
   * stopped the retry meanwhile. When the server cannot be asked the retry goes ahead — it tells
   * the server it is one, which is all that can be done.
   */
  private isStillUnanswered = async (turn: SentTurn): Promise<boolean> => {
    // Locked like a turn that is being sent: a second click or Enter finds it running, and
    // Stop ends it.
    this.status = 'sending'
    this.cancelConversationLoad()
    const token = this.loadToken
    let stored: AssistantChatMessage[] | null = null
    try {
      stored = await this.fetchMessages(turn.conversationId, true)
    } catch {
      // Out of reach: the retry itself will say so if it still is.
    }
    return runInAction(() => {
      if (
        token !== this.loadToken ||
        this.status !== 'sending' ||
        this.lastTurn !== turn
      ) {
        return false
      }
      if (stored && this.applyStoredTurn(turn, stored)) {
        this.status = 'idle'
        return false
      }
      return true
    })
  }

  /**
   * The user decided on a confirmation card. The server runs the action (never the model),
   * marks the card and returns the assistant's follow-up message.
   *
   * When that answer does not arrive, nothing is assumed about the action: the conversation is
   * read again from the server, which knows, and the card stays locked until it has been. The
   * same when the server answers that the action is being executed by another request.
   */
  decideAction = async (actionId: string, confirm: boolean) => {
    if (this.isInputLocked) {
      return
    }
    this.actionInFlight = actionId
    // A warning about an earlier attempt says nothing about this one.
    this.errorCode = null
    const conversationId = this.activeConversationId
    // The result belongs to the messages on screen now. A new chat or another conversation
    // opened meanwhile moves the token on: the server has recorded the outcome, and it shows
    // when this conversation is opened again. Taking a new token also drops the check of a
    // cut-off turn that is under way: its copy of the messages may predate this decision.
    this.cancelConversationLoad()
    const token = this.loadToken
    const isStale = () => token !== this.loadToken
    let reconcile = false
    try {
      const res = confirm
        ? await ApiService.Assistant.ConfirmAction(actionId)
        : await ApiService.Assistant.CancelAction(actionId)
      runInAction(() => {
        if (isStale()) {
          return
        }
        const status = String(res.data?.status ?? '')
        for (const message of this.messages) {
          message.parts = withActionStatus(message.parts, actionId, status)
        }
        if (res.data?.followUp) {
          this.messages.push(toChatMessage(res.data.followUp))
        } else if (isExecuting(status)) {
          // Another tab or request is running it: its outcome is on the server, not here.
          reconcile = true
        }
      })
    } catch (error) {
      runInAction(() => {
        if (isStale()) {
          return
        }
        reconcile = true
        // A refusal the server answered with (4xx) has been shown in its own words by the
        // response interceptor; only an outcome nobody reported gets the warning here.
        if (isOutcomeUnknown(error)) {
          this.errorCode = 'ACTION_UNCONFIRMED'
        }
      })
    }
    try {
      if (reconcile && conversationId && !isStale()) {
        await this.reconcileMessages(conversationId, isStale)
      }
    } finally {
      runInAction(() => {
        this.actionInFlight = null
      })
    }
  }

  /**
   * Stops whatever is in flight, at once: the state is settled here, and the aborted request
   * finds nothing left to do when it returns. A streaming answer keeps what arrived; a
   * question that never reached the server goes back to the composer, and a clarification
   * whose answer got nothing back can be answered again.
   */
  stop = () => {
    this.abortController?.abort()
    this.abortController = null
    this.cancelConversationLoad()
    this.restoreUnsentDraft()
    this.reopenClarification()
    this.finalizePartial()
  }

  /**
   * The session is dropped (evicted from the cache, or the user's session ended): its stream is
   * aborted, its timers, its waiting frame and its screen reaction are let go. Nothing of it is
   * shown again, so nothing is handed back to a composer.
   */
  dispose = () => {
    this.disposeContextReaction?.()
    this.disposeContextReaction = null
    this.placementHistory?.dispose()
    this.abortController?.abort()
    this.abortController = null
    this.cancelConversationLoad()
    this.dropTurnEvents()
    this.turn = initialTurn()
    this.status = 'idle'
  }

  /**
   * The turn that answered a clarification ended before the server sent anything back and was
   * refused before it was stored (busy conversation, a limit, the network) or was stopped: the
   * answer is not on record, so the question is open again exactly as before, option cards
   * included. Not after an error of the turn engine itself (see onError in dispatchTurn).
   */
  private reopenClarification = () => {
    const answering = this.answeringClarification
    this.answeringClarification = null
    if (!answering) {
      return
    }
    answering.prompt.clarificationAnswered = false
    answering.prompt.clarificationSelectedValue = undefined
    const index = answering.answer
      ? this.messages.indexOf(answering.answer)
      : -1
    if (index >= 0) {
      this.messages.splice(index, 1)
    }
    if (answering.draft) {
      this.giveBackDraft(answering.draft)
      if (this.lastTurn && this.lastTurn.question === answering.answer) {
        this.lastTurn.handedBack = answering.draft
      }
    }
  }

  /** Hands a question that was never sent back to the composer, with its tagged records. */
  private restoreUnsentDraft = () => {
    if (!this.unsentDraft) {
      return
    }
    this.giveBackDraft(this.unsentDraft)
    this.unsentDraft = null
  }

  /**
   * Rates an answer (1 / -1), adds the reason and the note of a rating, or takes the rating back
   * (0). The mark changes at once; it is what the server holds again as soon as every request
   * for that answer has been answered, so a rating that was not stored never stays on screen.
   * Resolves to whether this request was stored - the caller tells the user when it was not.
   */
  submitFeedback = async (
    messageId: string,
    rating: AssistantFeedbackRating,
    details?: AssistantFeedbackDetails
  ): Promise<boolean> => {
    const message = this.messages.find(m => m.id === messageId)
    if (!message) {
      return false
    }
    const feedbackRequests = this.store.feedbackRequests
    let state = feedbackRequests.get(messageId)
    if (!state) {
      state = { stored: message.feedbackRating, pending: 0, last: null }
      feedbackRequests.set(messageId, state)
    }
    const requests = state
    message.feedbackRating = rating === 0 ? null : rating
    requests.pending++

    // One request per answer at a time, in the order they were made: the reason of a rating is
    // sent while the rating itself may still be on its way.
    const before = requests.last
    const send = async () => {
      if (before) {
        await before
      }
      try {
        await ApiService.Assistant.SubmitFeedback(messageId, {
          rating,
          ...details,
        })
        requests.stored = rating === 0 ? null : rating
        return true
      } catch {
        return false
      }
    }
    const request = send()
    requests.last = request
    const stored = await request

    runInAction(() => {
      requests.pending--
      if (requests.pending > 0) {
        return
      }
      feedbackRequests.delete(messageId)
      const shown = this.messages.find(m => m.id === messageId)
      if (shown) {
        shown.feedbackRating = requests.stored
      }
    })
    return stored
  }

  private dispatchTurn = async (
    content: string,
    clarificationResponse?: {
      messageId: string
      selectedValue?: string
      freeText?: string
    },
    command?: string,
    mentions?: { entityType: string; id: string; label: string }[],
    /** What goes back to the composer if the question never reaches the server. */
    draft: DraftSnapshot | null = null,
    /** Retry: the question of the failed turn, already on screen. */
    retryOf: AssistantChatMessage | null = null,
    /** Retry: the record that question was asked about. */
    retryContext: AssistantPageContext | null = null
  ) => {
    // What is on screen as the question is asked, not when the conversation has been created.
    const pageContext = retryOf ? retryContext : this.activePageContext
    // Lock synchronously, before the first await: callers only check isBusy once, so a
    // double Enter/click during CreateConversation would otherwise start two turns.
    this.status = 'sending'
    this.errorCode = null
    this.serverError = null
    this.turn = initialTurn()
    this.dropTurnEvents()
    this.cancelConversationLoad()
    // Created before the first await so Stop can cancel every step of the turn.
    const controller = new AbortController()
    const { signal } = controller
    this.abortController = controller

    let conversationId = this.activeConversationId
    if (!conversationId && this.spareConversationId) {
      conversationId = this.spareConversationId
      this.activeConversationId = conversationId
      this.spareConversationId = null
    }
    if (!conversationId) {
      // Nothing is on screen or on the server yet: until the conversation exists, the
      // question only lives here.
      this.unsentDraft = draft
      try {
        const origin = this.conversationOrigin()
        const res = origin
          ? await ApiService.Assistant.CreateConversation(signal, origin)
          : await ApiService.Assistant.CreateConversation(signal)
        if (signal.aborted) {
          // stop() has already settled the state. The conversation exists and is empty: keep
          // it for the next first message.
          runInAction(() => {
            this.spareConversationId = res.data?.id ?? null
          })
          return
        }
        const createdId: string = res.data.id
        conversationId = createdId
        runInAction(() => {
          this.activeConversationId = createdId
          this.unsentDraft = null
        })
      } catch (error: any) {
        if (signal.aborted) {
          return
        }
        // Refused because the assistant is not available (any more): said as that, not as a
        // conversation that could not be started.
        const refusal = error?.response?.data?.error?.code
        const unavailable =
          typeof refusal === 'string' && AVAILABILITY_ERROR_CODES.has(refusal)
        runInAction(() => {
          this.restoreUnsentDraft()
          this.abortController = null
          this.errorCode = unavailable ? refusal : 'CONVERSATION_CREATE_FAILED'
          this.status = 'idle'
          if (unavailable) {
            this.store.reloadCapabilities()
          }
        })
        return
      }
    }

    let question: AssistantChatMessage | undefined
    const turnConversationId = conversationId
    // Whether the server has sent anything of this turn: until it has, it may have refused it.
    let received = false
    runInAction(() => {
      if (retryOf) {
        question = retryOf
      } else {
        this.messages.push({ role: 'User', content })
        question = this.messages[this.messages.length - 1]
        if (this.answeringClarification) {
          this.answeringClarification.answer = question
        }
      }
      this.lastTurn = {
        conversationId: turnConversationId,
        content,
        clarificationResponse,
        command,
        mentions,
        pageContext,
        question,
        place: placeOfQuestion(this.messages, question),
      }
    })

    // After Stop the stream may still deliver what it had buffered: that turn is over.
    const whileRunning = (apply: () => void) =>
      runInAction(() => {
        if (!signal.aborted) {
          apply()
        }
      })

    await streamAssistantMessage(
      conversationId,
      {
        content,
        clarificationResponse,
        pageContext: !pageContext
          ? undefined
          : pageContext.list
          ? {
              listKind: pageContext.type,
              serviceType: pageContext.list.serviceType,
            }
          : { recordType: pageContext.type, recordId: pageContext.id },
        // Read by a server that does not know pageContext yet.
        caseContextId:
          pageContext?.type === 'case' && !pageContext.list
            ? pageContext.id
            : undefined,
        command,
        mentions: mentions?.length ? mentions : undefined,
        retry: retryOf ? true : undefined,
        placement: this.serverPlacement,
      },
      {
        signal,
        onEvent: event =>
          whileRunning(() => {
            // The server is working on the turn: a clarification answer is on record.
            this.answeringClarification = null
            received = true
            if (event.type === 'token' || event.type === 'status') {
              this.status = 'streaming'
            }
            this.queueTurnEvent(event)
          }),
        onDone: ({ messageId }) =>
          whileRunning(() => this.finalizeTurn(messageId)),
        // About the record that was sent, not about the turn's state: applied also after Stop.
        onContext: verdict =>
          runInAction(() => this.applyContextVerdict(pageContext, verdict)),
        // After done, and about the conversation rather than the turn: applied whatever the
        // turn's own state is by now (the next question may already be streaming).
        onTitle: ({ conversationId: titledId, title }) =>
          runInAction(() => this.store.applyTitle(titledId, title)),
        onError: (code, message) =>
          whileRunning(() => {
            if (TURN_ENGINE_ERROR_CODES.has(code)) {
              // The turn engine failed after it stored the question: an answer to a
              // clarification is on record. Its bubble stays and Retry sends it again; opening
              // the card again would put a second answer on record.
              this.answeringClarification = null
            } else {
              this.reopenClarification()
              if (
                !received &&
                draft &&
                question &&
                isRefusedBeforeStored(code)
              ) {
                // Refused before anything of it was stored (too long, a limit, the session):
                // the question is not in the conversation, so it does not stay on screen as if
                // it were. It goes back to the composer, to be shortened or sent later.
                const index = this.messages.indexOf(question)
                if (index >= 0) {
                  this.messages.splice(index, 1)
                }
                this.lastTurn = null
                this.giveBackDraft(draft)
              }
            }
            this.finalizePartial()
            this.errorCode = code
            const said = typeof message === 'string' ? message.trim() : ''
            this.serverError = said ? { code, message: said } : null
            if (code === 'CONTENT_FILTERED' && question) {
              // As after a reload: the server leaves a blocked question out of later turns.
              question.excludedFromContext = true
            }
            if (RECONCILED_ERROR_CODES.has(code) && this.lastTurn) {
              this.reconcileTurn(this.lastTurn)
            }
            if (
              AVAILABILITY_ERROR_CODES.has(code) ||
              code === PRIVACY_NOTICE_REQUIRED
            ) {
              // Switched off after this page was loaded, or a privacy notice this page has not
              // shown yet: the capabilities say which, so they are read again now instead of
              // letting the next question find out the same way.
              this.store.reloadCapabilities()
            }
          }),
      }
    )
  }

  /**
   * Takes an event of the running turn. Text arrives a few characters at a time, many times per
   * frame, and every change of `turn` renders the growing answer again (markdown and all): text
   * is collected and applied once per animation frame. Anything else (a status, a part,
   * citations, a clarification) is applied at once, after the text that came before it.
   */
  private queueTurnEvent = (event: TurnEvent) => {
    this.pendingEvents.push(event)
    if (event.type !== 'token') {
      this.flushTurnEvents()
    } else if (!this.cancelFrame) {
      this.cancelFrame = onNextFrame(this.flushTurnEvents)
    }
  }

  /** Applies the collected events to `turn`, in the order they arrived. */
  private flushTurnEvents = () => {
    const events = this.pendingEvents
    this.dropTurnEvents()
    if (events.length) {
      this.turn = events.reduce(turnReducer, this.turn)
    }
  }

  private dropTurnEvents = () => {
    this.cancelFrame?.()
    this.cancelFrame = null
    this.pendingEvents = []
  }

  private finalizeTurn = (messageId: string) => {
    this.flushTurnEvents()
    this.answeringClarification = null
    this.lastTurn = null
    const message = turnToMessage(this.turn, messageId)
    if (message) {
      this.messages.push(message)
    }
    this.turn = initialTurn()
    this.status = 'idle'
    this.store.refreshConversationsAfterTurn(this) // refresh titles / ordering
  }

  /** Stream stopped early: keep what arrived as a partial answer. */
  private finalizePartial = () => {
    // Text still waiting for its frame is part of what arrived.
    this.flushTurnEvents()
    const message = turnToMessage(this.turn)
    if (message) {
      this.messages.push(message)
    }
    this.turn = initialTurn()
    this.status = 'idle'
  }
}
